import { Component, EventEmitter, Input, OnInit, Output, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { forkJoin, of, map } from 'rxjs';

import { DocumentService } from '../../../core/services/document/document.service';
import { ToastService } from '../../../core/services/toast.service';
import {
  DocumentGroup,
  groupDocumentsByDate,
  groupDocumentsByWound,
  PatientDocument,
  WoundDocumentGroup,
} from '../../../core/models/document/document.model';
import { getDocumentTypeFromMime } from '../../../../enums/document-type.enum';
import { DocumentUploadComponent } from './components/document-upload/document-upload.component';
import { DocumentTimelineComponent } from './components/document-timeline/document-timeline.component';
import { DocumentPreviewComponent } from './components/document-preview/document-preview.component';

@Component({
  selector: 'app-document-manager',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    DocumentUploadComponent,
    DocumentTimelineComponent,
    DocumentPreviewComponent,
  ],
  templateUrl: './document-manager.component.html',
  styleUrl: './document-manager.component.scss',
})
export class DocumentManagerComponent implements OnInit, OnChanges {
  @Input() patientId?: string;
  @Input() woundId?: string;
  @Input() readOnlyDocumentUpload = false;
  @Input() readOnlyDocumentTimeline = false;
  @Input() readOnlyDocumentPreview = false;
  @Input() groupByWound = false;
  @Input() wounds: any[] = [];

  @Output() documentUploaded = new EventEmitter<PatientDocument>();

  documents: any[] = [];
  documentGroups: DocumentGroup[] = [];
  woundDocumentGroups: WoundDocumentGroup[] = [];
  isLoading = false;
  isUploading = false;

  previewDocument: PatientDocument | null = null;
  showPreview = false;

  constructor(
    private documentService: DocumentService,
    private toastService: ToastService,
    private translateService: TranslateService
  ) {}

  ngOnInit(): void {
    this.loadDocuments();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['patientId'] || changes['woundId'] || changes['wounds']) {
      this.loadDocuments();
    } else if (changes['groupByWound']) {
      this.updateGroups();
    }
  }

  loadDocuments(): void {
    if (!this.patientId && !this.woundId) return;
    this.isLoading = true;

    if (this.woundId) {
      this.documentService.getDocumentsByWound(this.woundId).subscribe({
        next: (documents) => {
          this.documents = documents;
          this.updateGroups();
          this.isLoading = false;
        },
        error: () => (this.isLoading = false),
      });
    } else {
      // Fetch patient documents
      const patientDocs$ = this.documentService.getDocumentsByPatient(this.patientId!);
      
      // Collect all wound IDs (including follow-ups) to fetch their documents
      const allWoundIds: string[] = [];
      const collectIds = (list: any[]) => {
        list.forEach(w => {
          if (w.id) allWoundIds.push(w.id);
          if (w.followUps) collectIds(w.followUps);
        });
      };
      
      if (this.groupByWound && this.wounds && this.wounds.length > 0) {
        collectIds(this.wounds);
        const uniqueWoundIds = [...new Set(allWoundIds)];
        const woundRequests = uniqueWoundIds.map(id => 
          this.documentService.getDocumentsByWound(id).pipe(
            map(docs => (docs || []).map(d => ({ ...d, woundId: id })))
          )
        );
        
        forkJoin([patientDocs$, ...woundRequests]).subscribe({
          next: (results) => {
            const allDocs = results.flat() as any[];
            const docMap = new Map<string, any>();
            allDocs.forEach(item => {
              if (item && item.id) {
                const existing = docMap.get(item.id);
                // If we already have it from a wound request, it has a woundId. 
                // If we get it from patientDocs later, don't overwrite the one with woundId.
                if (!existing || (!existing.woundId && item.woundId)) {
                  docMap.set(item.id, item);
                }
              }
            });
            this.documents = Array.from(docMap.values());
            this.updateGroups();
            this.isLoading = false;
          },
          error: () => (this.isLoading = false),
        });
      } else {
        patientDocs$.subscribe({
          next: (documents) => {
            this.documents = documents;
            this.updateGroups();
            this.isLoading = false;
          },
          error: () => (this.isLoading = false),
        });
      }
    }
  }

  updateGroups(): void {
    if (this.groupByWound) {
      this.woundDocumentGroups = groupDocumentsByWound(
        this.documents as any,
        this.wounds
      );
    } else {
      this.documentGroups = groupDocumentsByDate(this.documents as any);
    }
  }

  onFilesSelected(files: File[]): void {
    this.uploadDocuments(files);
  }

  onUploadError(errorKey: string): void {
    const message = this.translateService.instant(errorKey);
    this.toastService.showToast(message, 'error');
  }

  private uploadDocuments(files: File[]): void {
    this.isUploading = true;
    let uploadCount = 0;
    const totalFiles = files.length;

    files.forEach((file) => {
      this.documentService
        .uploadDocument({
          file,
          patientId: this.patientId!,
          woundId: this.woundId,
        })
        .subscribe({
          next: (response) => {
            if (response.isSuccess && response.data) {
              const newDoc = {
                ...response.data,
                fileType: getDocumentTypeFromMime(file.type) || 'unknown',
                fileSize: file.size,
                mimeType: file.type,
                uploadedByName: '', // Will be filled by backend/refresh
                woundId: this.woundId,
              };
              this.documents.unshift(newDoc);
              this.updateGroups();
              this.documentUploaded.emit(newDoc as any);
            }
            uploadCount++;
            if (uploadCount === totalFiles) {
              this.isUploading = false;
              const message = this.translateService.instant(
                'documents.uploadSuccess'
              );
              this.toastService.showToast(message, 'success');
            }
          },
          error: () => {
            uploadCount++;
            if (uploadCount === totalFiles) {
              this.isUploading = false;
            }
          },
        });
    });
  }

  onDeleteRequested(document: PatientDocument): void {
    this.deleteDocument(document.id);
  }

  private deleteDocument(documentId: string): void {
    this.isLoading = true;
    this.documentService.deleteDocument(documentId).subscribe({
      next: (response) => {
        if (response.isSuccess) {
          this.documents = this.documents.filter((d) => d.id !== documentId);
          this.updateGroups();
          this.toastService.showToast(
            this.translateService.instant('documents.deleteSuccess'),
            'success'
          );
        }
        this.isLoading = false;
      },
      error: () => {
        this.toastService.showToast(
          this.translateService.instant('documents.deleteError'),
          'error'
        );
        this.isLoading = false;
      },
    });
  }

  onPreviewRequested(document: PatientDocument): void {
    this.previewDocument = document;
    this.showPreview = true;
  }

  onDownloadRequested(document: PatientDocument): void {
    this.documentService.downloadDocument(document.id).subscribe({
      next: (blob) => {
        const url = window.URL.createObjectURL(blob);
        const link = window.document.createElement('a');
        link.href = url;
        link.download = document.originalFileName || 'document';
        link.click();
        window.URL.revokeObjectURL(url);
      },
      error: (error: any) => {
        console.log('error downloading document', error);
        const message = this.translateService.instant(
          'documents.downloadError'
        );
        this.toastService.showToast(message, 'error');
      },
    });
  }

  closePreview(): void {
    this.showPreview = false;
    this.previewDocument = null;
  }
}
